<script setup>
/* global google */
import { ref, reactive, computed, onMounted } from 'vue'
import DoughnutChart from './components/DoughnutChart.vue'
import citiesIcon from './../img/cities.svg'
import chartsIcon from './../img/charts.svg'

const cfg         = window.ebusFleet || {}
const lang        = cfg.lang || ''
const postId      = cfg.postId || 0

const i18nAll        = cfg.i18n || {}
const infoDateAll    = cfg.infoDate || {}
const regionLabelAll = cfg.regionLabel || {}
const currentLang    = ref((document.documentElement.lang || lang || 'en').substring(0, 2))
const i18n           = reactive({ ...(i18nAll[currentLang.value] || i18nAll['en'] || {}) })
const infoDate       = computed(() => infoDateAll[currentLang.value]    || infoDateAll['en']    || '')
const regionLabel    = computed(() => regionLabelAll[currentLang.value] || regionLabelAll['en'] || '')
const exportUrl      = computed(() => {
    const params = ['lang=' + currentLang.value]
    if (postId) params.push('page_id=' + postId)
    return apiBase + 'export?' + params.join('&')
})

function countryLabel(country) {
    return (country.names || {})[currentLang.value] || country.name
}

function cityLabel(city) {
    return (city.names || {})[currentLang.value] || city.title
}
const apiBase     = (cfg.apiUrl || '').replace(/\/?$/, '/')

function apiUrl(path) {
    const params = []
    const currentLang = document.documentElement.lang || lang
    if (currentLang) params.push('lang=' + currentLang)
    if (postId)      params.push('page_id=' + postId)
    return apiBase + path + (params.length ? '?' + params.join('&') : '')
}

// ── Reactive state ────────────────────────────────────────────────────────────
const panel          = ref('data')    // 'data' | 'list'
const scope          = ref(null)      // null | 'latin-america' | 'country' | 'city'
const activeChart    = ref('vehicle') // 'vehicle' | 'manufactor'
const mobileOverlay  = ref(null)      // null | 'sobre' | 'contato'
const mapReady       = ref(false)
const placeData      = ref(null)
const breadcrumb     = ref(null)      // { name, slug, id } | null
const countries      = ref(cfg.countries || [])
const typeChartData      = ref([])
const mfrChartData       = ref([])
const mfrChartDataI18n   = computed(() =>
    mfrChartData.value.map(d => d.nome === 'OTHERS' ? { ...d, nome: i18n.others || 'OTHERS' } : d)
)
const aboutContent   = ref('')
const contactContent = ref('')

// ── Map state (imperative) ────────────────────────────────────────────────────
const mapEl = ref(null)
let map             = null
let mapBounds       = null
const countryMarkers = []
const countryLabels  = []
const cityMarkers    = []
let zoom_out        = 3
let zoom_in         = 6
let zoomChangedByJS = false
let latAmController = null
let countryController = null
let cityController  = null
let showCard        = false

// ── Computed ──────────────────────────────────────────────────────────────────
const placeName = computed(() => {
    const d = placeData.value
    if (!d) return ''
    if (d.city) return d.city
    if (scope.value === 'latin-america') return regionLabel || d.country || ''
    return d.country || ''
})

const co2Available = computed(() =>
    placeData.value && !isNaN(placeData.value.co2) && placeData.value.co2
)

const co2Formatted = computed(() => {
    if (!co2Available.value) return ''
    let co2 = placeData.value.co2.toFixed(2) + ' kt'
    if (lang !== 'en') co2 = co2.replace(/\./g, ',')
    return co2
})

function fmtNumber(n) {
    const s = String(n)
    return s.length > 3 ? s.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : s
}

function fmtPercent(elec, total) {
    let pct = (elec * 100 / total).toFixed(2) + '%'
    if (lang !== 'en') pct = pct.replace(/\./g, ',')
    return pct
}

const frotaPercent = computed(() => {
    const d = placeData.value
    if (!d || !d.frota_total) return ''
    return fmtPercent(d.frota_elec, d.frota_total)
})

const frotaTotal = computed(() =>
    placeData.value?.frota_total ? fmtNumber(placeData.value.frota_total) : ''
)

const showFrotaLatam = computed(() =>
    scope.value === 'latin-america' && !isNaN(placeData.value?.frota_total) && placeData.value?.frota_total > 0
)
const showDeaths = computed(() =>
    scope.value === 'city' && (placeData.value?.preventable_deaths ?? 0) > 0
)
const showTender = computed(() =>
    scope.value === 'city' && (placeData.value?.tender_amount ?? 0) > 0
)

const deathsText = computed(() => {
    if (!showDeaths.value) return ''
    const n = fmtNumber(placeData.value.preventable_deaths)
    return (i18n.preventableDeaths || '').replace('<span></span>', `<span>${n}</span>`)
})

const tenderText = computed(() => {
    if (!showTender.value) return ''
    const n = fmtNumber(placeData.value.tender_amount)
    return (i18n.tenderAmount || '').replace('<span></span>', `<span>${n}</span>`)
})

// ── Icon / map helpers ────────────────────────────────────────────────────────
function isSmall()  { return window.innerHeight < 600 || window.innerWidth < 450 }
function isMedium() { return window.innerHeight < 1000 }

function iconSize() {
    if (isSmall())  return new google.maps.Size(8, 8)
    if (isMedium()) return new google.maps.Size(15, 15)
    return new google.maps.Size(20, 20)
}

function getLabelPosition(text, position) {
    const sz = isSmall()
    if (position === 'acima')    return new google.maps.Point(sz ? 8 : 10, sz ? -8 : -10)
    if (position === 'direita') {
        const l = sz ? text.length * 2.4 + 15 : text.length * 3.7 + 20
        return new google.maps.Point(l, sz ? 4 : 8)
    }
    if (position === 'esquerda') {
        const l = sz ? text.length * 2.5 + 5 : text.length * 3 + text.length + 5
        return new google.maps.Point(-l, sz ? 4 : 8)
    }
    return new google.maps.Point(sz ? 8 : 10, sz ? 15 : 25)
}

const SVG_FALLBACKS = {
    'country-marker':       'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12"><circle cx="6" cy="6" r="6" fill="#555555"/></svg>'),
    'city-marker':          'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="8" height="8"><circle cx="4" cy="4" r="4" fill="#222222"/></svg>'),
    'selected-city-marker': 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="8" height="8"><circle cx="4" cy="4" r="4" fill="#e85d04"/></svg>'),
}

function buildMarkerIcon(urlKey) {
    const sz = iconSize()
    const urlMap = {
        'country-marker':       cfg.countryMarkerUrl,
        'city-marker':          cfg.cityMarkerUrl,
        'selected-city-marker': cfg.selectedCityMarkerUrl,
    }
    return {
        url:         String(urlMap[urlKey] || '') || SVG_FALLBACKS[urlKey] || SVG_FALLBACKS['city-marker'],
        scaledSize:  sz,
        origin:      new google.maps.Point(0, 0),
        anchor:      new google.maps.Point(0, 0),
        labelOrigin: new google.maps.Point(10, 30),
    }
}

function buildCityMarkerIcon(selected = false) {
    const fill = selected ? '#e85d04' : '#222222'
    const svg  = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12"><circle cx="6" cy="6" r="6" fill="${fill}"/></svg>`
    return {
        url:         'data:image/svg+xml;charset=UTF-8;base64,' + btoa(svg),
        scaledSize:  new google.maps.Size(12, 12),
        origin:      new google.maps.Point(0, 0),
        anchor:      new google.maps.Point(6, 6),
        labelOrigin: new google.maps.Point(6, 6),
    }
}

function buildCountryMarkerIcon(numCities) {
    const px  = numCities < 10 ? 23.5 : 19.5
    const svg = `<?xml version="1.0" encoding="UTF-8" standalone="no"?><svg width="28px" height="28px" viewBox="0 0 60 60" version="1.1" xmlns="http://www.w3.org/2000/svg"><g><circle style="fill:#000000;stroke-width:1.37578;stroke-linecap:square;stroke-dasharray:3.37578, 6.75156" cx="30.2265" cy="30.239048" r="18.713215"/><circle style="fill:#ffffff;stroke-width:1.64551;stroke-linecap:square;stroke-dasharray:2.64551, 5.29101" cx="30.2265" cy="30.239048" r="23.285437"/><text xml:space="preserve" style="font-size:20.7467px;font-family:'Arial';text-align:center;fill:#000000" x="${px}" y="38">${numCities}</text></g></svg>`
    return {
        url:         'data:image/svg+xml;charset=UTF-8;base64,' + btoa(svg),
        scaledSize:  new google.maps.Size(28, 28),
        origin:      new google.maps.Point(0, 0),
        anchor:      new google.maps.Point(7, 14),
        labelOrigin: new google.maps.Point(7, 14),
    }
}

// ── Country label overlay ─────────────────────────────────────────────────────
let CountryLabel

function initCountryLabelClass() {
    function Label(position, title) {
        this.position_ = position
        this.title_    = title
        this.div_      = null
        this.setMap(map)
    }
    Label.prototype = Object.create(google.maps.OverlayView.prototype)
    Label.prototype.onAdd = function () {
        const div = document.createElement('div')
        div.className = 'country-title__overlay'
        Object.assign(div.style, {
            borderStyle:   'none',
            borderWidth:   '0',
            position:      'absolute',
            fontFamily:    'ibm-plex-mono',
            textAlign:     'center',
            color:         'rgba(0,0,0,1)',
            width:         '500px',
            height:        '20px',
            transform:     'translateX(-250px)',
            textTransform: 'uppercase',
            marginTop:     isSmall() ? '8px'  : isMedium() ? '15px' : '22px',
            marginLeft:    isSmall() ? '6px'  : isMedium() ? '8px'  : '10px',
            fontSize:      isSmall() ? '8px'  : isMedium() ? '12px' : '13px',
            pointerEvents: 'none',
        })
        div.appendChild(document.createTextNode(this.title_))
        this.div_ = div
        this.getPanes().overlayLayer.appendChild(div)
    }
    Label.prototype.draw = function () {
        const proj = this.getProjection()
        if (!proj) return
        const pos = proj.fromLatLngToDivPixel(this.position_)
        if (!pos) return
        Object.assign(this.div_.style, { left: pos.x + 'px', top: pos.y + 'px' })
    }
    Label.prototype.onRemove = function () {
        this.div_.parentNode.removeChild(this.div_)
        this.div_ = null
    }
    CountryLabel = Label
}

// ── Map init ──────────────────────────────────────────────────────────────────
function initMap() {
    if (!mapEl.value) return
    if (isSmall() || window.innerWidth < 992) { zoom_out = 3; zoom_in = 5 }
    initCountryLabelClass()

    mapBounds = cfg.mapBounds || { north: 31.653381, south: -55.973798, west: -116.458575, east: -34.583324 }
    const center = cfg.mapCenter || { lat: -12.046373, lng: -74.042755 }

    map = new google.maps.Map(mapEl.value, {
        center,
        restriction:            { latLngBounds: mapBounds, strictBounds: false },
        zoom:                   zoom_out,
        disableDefaultUI:       true,
        scrollwheel:            true,
        disableDoubleClickZoom: true,
        backgroundColor:        '#EDEDED',
        mapTypeId:              google.maps.MapTypeId.ROADMAP,
        styles: [
            { featureType: 'water',       elementType: 'geometry',        stylers: [{ visibility: 'on' }, { color: '#ededed' }, { saturation: -100 }] },
            { featureType: 'landscape',   elementType: 'geometry',        stylers: [{ visibility: 'on' }, { lightness: -10 }, { saturation: -80 }] },
            { featureType: 'road',                                         stylers: [{ visibility: 'off' }] },
            { featureType: 'poi',                                          stylers: [{ visibility: 'off' }] },
            { featureType: 'administrative',                               stylers: [{ visibility: 'off' }] },
            { featureType: 'administrative.country', elementType: 'geometry.stroke', stylers: [{ visibility: 'on' }, { color: '#777777' }] },
            {                             elementType: 'labels',           stylers: [{ visibility: 'off' }] },
        ],
    })

    const wkt = cfg.wkt || ''
    if (wkt) {
        const match = wkt.match(/POLYGON\s*\(\((.*?)\)\)/)
        if (match) {
            const coords = match[1].split(', ').map(coord => {
                const [lng, lat] = coord.split(' ').map(Number)
                return { lat, lng }
            })
            new google.maps.Polygon({
                paths: coords, strokeColor: '#EDEDED', strokeOpacity: 1, strokeWeight: 1,
                fillColor: '#EDEDED', fillOpacity: 1, map,
            })
        }
    }

    loadCountryMarkers()
    loadCityMarkers()

    map.addListener('zoom_changed', () => {
        if (zoomChangedByJS) { zoomChangedByJS = false; return }
        if (window.innerWidth < 992) {
            map.getZoom() < 4 ? hideCityMarkers() : showCityMarkers()
        }
    })
}

// ── Markers ───────────────────────────────────────────────────────────────────
async function loadCountryMarkers() {
    const data = await fetch(apiUrl('country-markers')).then(r => r.json())
    const visible = new google.maps.LatLngBounds()

    data.forEach((item, i) => {
        const icon = buildCountryMarkerIcon(item.num_cities)
        countryMarkers[i] = new google.maps.Marker({
            position: { lat: item.lat, lng: item.lng },
            map, icon, title: item.title,
            country: item.title, country_id: item.country_id,
        })
        const pos      = new google.maps.LatLng(item.lat, item.lng)
        const labelPos = isSmall()
            ? new google.maps.LatLng(item.lat - 0.7, item.lng)
            : pos
        countryLabels[i] = new CountryLabel(labelPos, item.title)
        visible.extend(pos)
        countryMarkers[i].addListener('click', function () {
            showCard = false
            onCountryClick(this.country_id, this, false)
        })
    })

    zoomChangedByJS = true
    mapBounds = visible
    map.fitBounds(visible)
    const b   = visible.toJSON()
    const pad = 5
    map.setOptions({ restriction: { latLngBounds: {
        north: Math.min(90,  b.north + pad),
        south: Math.max(-90, b.south - pad),
        east:  b.east  + pad,
        west:  b.west  - pad,
    }, strictBounds: false } })
    mapReady.value = true
}

async function loadCityMarkers() {
    const data = await fetch(apiUrl('city-markers')).then(r => r.json())
    if (!data.length) {
        console.warn('[ebus-radar] city-markers returned 0 results. Check that cities have localizacao_marcador and frota_total set, and that the lang param matches the data language.')
        return
    }
    const fontSize = isSmall() ? '8px' : isMedium() ? '12px' : '13px'
    const icon     = buildCityMarkerIcon()

    data.forEach((item, i) => {
        const ico = { ...icon, labelOrigin: getLabelPosition(item.label, item.label_position) }
        cityMarkers[i] = new google.maps.Marker({
            position: { lat: item.lat, lng: item.lng },
            label:    { color: 'rgba(0,0,0,0)', fontFamily: 'ibm-plex-mono', fontSize, text: String(item.label) },
            map:      null, icon: ico, title: '',
        })
        cityMarkers[i].label_position = item.label_position
        cityMarkers[i].city           = item.city
        cityMarkers[i].selected       = false

        cityMarkers[i].addListener('mouseover', function () {
            const lbl = this.getLabel(); lbl.color = 'rgba(0,0,0,1)'; this.setLabel(lbl)
        })
        cityMarkers[i].addListener('mouseout', function () {
            if (this.selected) return
            const lbl = this.getLabel(); lbl.color = 'rgba(0,0,0,0)'; this.setLabel(lbl)
        })
        cityMarkers[i].addListener('click', function () {
            showCard = false
            const cityIco    = buildCityMarkerIcon()
            const selCityIco = buildCityMarkerIcon(true)
            cityMarkers.forEach(cm => {
                const ic = { ...cityIco, labelOrigin: getLabelPosition(cm.getLabel().text, cm.label_position) }
                cm.setIcon(ic)
                const lbl = cm.getLabel(); lbl.color = 'rgba(0,0,0,0)'; cm.setLabel(lbl)
                cm.selected = false
            })
            const lbl = this.getLabel()
            const ic  = { ...selCityIco, labelOrigin: getLabelPosition(lbl.text, this.label_position) }
            this.setIcon(ic); lbl.color = 'rgba(0,0,0,1)'; this.setLabel(lbl); this.selected = true
            onCityClick(this.city)
        })
    })

    // Show immediately — hide only on mobile at the initial zoomed-out level
    if (window.innerWidth >= 992 || map.getZoom() >= 4) {
        showCityMarkers()
    }
}

function showCityMarkers() { cityMarkers.forEach(m => m.setMap(map)) }
function hideCityMarkers() { cityMarkers.forEach(m => m.setMap(null)) }

// ── Click handlers ────────────────────────────────────────────────────────────
function onCountryClick(countryId, marker, showingCity) {
    panel.value = 'data'
    zoomChangedByJS = true
    map.setZoom(zoom_in)
    map.setCenter(marker.getPosition())

    const cityIco = buildCityMarkerIcon()
    cityMarkers.forEach(cm => {
        const ic = { ...cityIco, labelOrigin: getLabelPosition(cm.getLabel().text, cm.label_position) }
        cm.setIcon(ic)
        const lbl = cm.getLabel(); lbl.color = 'rgba(0,0,0,0)'; cm.setLabel(lbl)
        cm.selected = false
    })

    if (!showingCity) {
        if (countryController) countryController.abort()
        countryController = new AbortController()
        fetch(apiUrl('country/' + countryId), { signal: countryController.signal })
            .then(r => r.json()).then(data => setPlaceData(data, false)).catch(() => {})
    }

    showCityMarkers()
}

function onCityClick(citySlug) {
    if (cityController) cityController.abort()
    cityController = new AbortController()
    fetch(apiUrl('city/' + citySlug), { signal: cityController.signal })
        .then(r => r.json()).then(data => setPlaceData(data, false)).catch(() => {})
}

// ── Data ──────────────────────────────────────────────────────────────────────
function setPlaceData(data, isLatinAmerica) {
    typeChartData.value = Object.values(data.chart_data || {})
    mfrChartData.value  = data.manufactors || []
    placeData.value     = data
    panel.value         = 'data'

    if (data.city) {
        scope.value      = 'city'
        breadcrumb.value = { name: data.country, slug: data.country_slug, id: data.country_id }
    } else if (isLatinAmerica) {
        scope.value      = 'latin-america'
        breadcrumb.value = null
    } else {
        scope.value      = 'country'
        breadcrumb.value = null
    }
}

function reloadLatinAmericaData() {
    breadcrumb.value = null
    if (map && mapBounds) {
        if (map.getZoom() !== zoom_out) { zoomChangedByJS = true; map.setZoom(zoom_out) }
        zoomChangedByJS = true
        map.fitBounds(mapBounds)
        if (cityMarkers.length && cityMarkers[0].getMap() === map) hideCityMarkers()
    }
    if (latAmController) latAmController.abort()
    latAmController = new AbortController()
    fetch(apiUrl('region'), { signal: latAmController.signal })
        .then(r => r.json()).then(data => setPlaceData(data, true)).catch(() => {})
}

// ── Panel navigation ──────────────────────────────────────────────────────────
function isMobileMenuVisible() {
    const el = document.querySelector('.mobile-menu')
    return el ? window.getComputedStyle(el).display !== 'none' : false
}

function showData()    { panel.value = 'data'; reloadLatinAmericaData() }
function showCities()  { panel.value = 'list' }
function showAbout()   { isMobileMenuVisible() ? (mobileOverlay.value = 'sobre')   : (panel.value = 'about') }
function showContact() { isMobileMenuVisible() ? (mobileOverlay.value = 'contato') : (panel.value = 'contact') }

function onBreadcrumbCountryClick() {
    if (!breadcrumb.value) return
    countryMarkers.forEach(cm => {
        if (breadcrumb.value.id === cm.country_id) onCountryClick(cm.country_id, cm, false)
    })
}

// ── Countries/cities list clicks ──────────────────────────────────────────────
function onCountryTitleClick(country) {
    panel.value = 'data'
    countryMarkers.forEach(cm => {
        if (country.id === cm.country_id) onCountryClick(country.id, cm, false)
    })
}

function onCityTitleClick(city, country) {
    panel.value = 'data'
    countryMarkers.forEach(cm => {
        if (country.id === cm.country_id) {
            onCountryClick(cm.country_id, cm, true)
            setTimeout(() => {
                const cityIco    = buildCityMarkerIcon()
                const selCityIco = buildCityMarkerIcon(true)
                cityMarkers.forEach(cim => {
                    const isTarget = city.slug === cim.city
                    const ic = { ...(isTarget ? selCityIco : cityIco), labelOrigin: getLabelPosition(cim.getLabel().text, cim.label_position) }
                    cim.setIcon(ic)
                    const lbl = cim.getLabel()
                    lbl.color = isTarget ? 'rgba(0,0,0,1)' : 'rgba(0,0,0,0)'
                    cim.setLabel(lbl)
                    cim.selected = isTarget
                })
                onCityClick(city.slug)
            }, 500)
        }
    })
}

// ── Zoom ──────────────────────────────────────────────────────────────────────
function zoomIn() {
    if (!map) return
    zoomChangedByJS = true
    const z = map.getZoom()
    if (z < 11) map.setZoom(z + 1)
    if (z > 5 && z < 8) showCityMarkers()
}

function zoomOut() {
    if (!map) return
    zoomChangedByJS = true
    const z = map.getZoom()
    if (z > 2) map.setZoom(z - 1)
    if (z < 7) hideCityMarkers()
    if (z >= 8) showCityMarkers()
}

// ── Map area click (show data panel) ─────────────────────────────────────────
function onSectionMousedown() {
    if (panel.value !== 'data') {
        showCard = true
        setTimeout(() => { if (showCard) showData() }, 350)
    }
}

// ── Boot ──────────────────────────────────────────────────────────────────────
onMounted(() => {    

    const hash = window.location.hash.slice(1)
    if (/cidades|ciudades|cities/.test(hash))       showCities()    

    const on = (sel, fn) => document.querySelector(sel)?.addEventListener('click', e => { e.preventDefault(); fn() })
    on('.america-latina a', () => { if (panel.value !== 'data') panel.value = 'data'; reloadLatinAmericaData() })    

    const mobileMenu     = document.querySelector('.mobile-menu')
    const mobileMenuCont = document.querySelector('.mobile-menu__container')
    const mobileClose    = document.querySelector('.close')
    if (mobileMenu && mobileMenuCont) {
        mobileMenu.addEventListener('click', () => {
            mobileMenuCont.style.display = 'flex'
            mobileMenuCont.classList.add('showing')
        })
        mobileClose?.addEventListener('click', () => {
            mobileMenuCont.classList.remove('showing')
            setTimeout(() => { mobileMenuCont.style.display = 'none' }, 350)
        })
    }

    new MutationObserver(() => {
        currentLang.value = (document.documentElement.lang || lang || 'en').substring(0, 2)
        Object.assign(i18n, i18nAll[currentLang.value] || i18nAll['en'] || {})
    }).observe(document.documentElement, { attributes: true, attributeFilter: ['lang'] })

    function boot() { initMap(); reloadLatinAmericaData() }
    if (typeof google !== 'undefined' && google.maps) {
        boot()
    } else {
        const poll = setInterval(() => {
            if (typeof google !== 'undefined' && google.maps) { clearInterval(poll); boot() }
        }, 100)
    }
})
</script>

<template>
<div>  
    
    <div class="section section--home row" @mousedown="onSectionMousedown">

        <div class="map-buttons__container">
            <button class="zoom zoom--in" @click="zoomIn">+</button>
            <button class="zoom zoom--out" @click="zoomOut">-</button>
        </div>

        <div class="map__container" id="map-container" :style="{ visibility: mapReady ? 'visible' : 'hidden' }">
            <div id="map" ref="mapEl"></div>
        </div>

        <div class="map__clicker"></div>

        <div class="side-content shadow-right-sidebar z-1 py-[25px] pl-8 pr-4 overflow-x-hidden" @mousedown.stop>

            <h1 class="font-mono text-4xl mb-10">{{ i18n.pageTitle }}</h1>

            <div class="flex gap-5 font-ibm-plex-sans text-xl mb-8">
                <div class="flex gap-2 items-center cursor-pointer" :class="panel !== 'data' ? 'opacity-50' : ''" @click="showData">
                    <img :src="chartsIcon" class="h-6 w-auto">
                    {{ i18n.tabCharts }}
                </div>
                <div class="flex gap-2 items-center cursor-pointer" :class="panel !== 'list' ? 'opacity-50' : ''" @click="showCities">
                    <img :src="citiesIcon" class="h-6 w-auto">
                    {{ i18n.tabCities }}
                </div>
            </div>


            <!-- Statistics panel -->
            <div class="data__container" v-show="panel === 'data'">

                <ul class="breadcrumbs" v-show="scope !== null" :class="{ 'no-city': scope !== 'city' }">
                    <li class="breadcrumb--la" @click="reloadLatinAmericaData">{{ regionLabel || i18n.latinAmerica }}</li>
                    <li class="breadcrumb--country" v-show="scope === 'city'" @click="onBreadcrumbCountryClick">
                        {{ breadcrumb?.name }}
                    </li>
                </ul>

                <div class="place__name">
                    <h1 class="data__country">{{ placeName }}</h1>
                </div>

                <div class="chart__types">
                    <div class="chart-types__container">
                        <div class="chart-type chart-type--vehicle"
                             :class="{ 'chart-type--active': activeChart === 'vehicle' }"
                             @click="activeChart = 'vehicle'">
                            {{ i18n.byVehicles }}
                        </div>
                        <div class="chart-type chart-type--manufactor"
                             :class="{ 'chart-type--active': activeChart === 'manufactor' }"
                             @click="activeChart = 'manufactor'">
                            {{ i18n.byManufacturers }}
                        </div>
                    </div>
                </div>

                <DoughnutChart
                    v-if="activeChart === 'vehicle'"
                    :chart-data="typeChartData"
                    chart-type="vehicle"
                    :frota-elec="placeData?.frota_elec"
                    :label="i18n.totalElectricBuses"
                />

                <DoughnutChart
                    v-if="activeChart === 'manufactor'"
                    :chart-data="mfrChartDataI18n"
                    chart-type="manufactor"
                    :frota-elec="placeData?.frota_elec"
                    :label="i18n.totalElectricBuses"
                />

                <div class="mobile-wrapper">                   

                    <div class="data__co2">
                        <h1 class="co2__amount" v-show="co2Available">{{ co2Formatted }}</h1>
                        <p class="co2__amount__text" v-show="co2Available" v-html="i18n.co2AvoidedPerYear"></p>
                        <p class="co2__amount__text--unavailable" v-show="placeData && !co2Available" v-html="i18n.co2Unavailable"></p>
                    </div>

                    <div class="data-row">
                        <div class="data__deaths data-row__item" v-show="showDeaths">
                            <p class="death__amount__text" v-html="deathsText"></p>
                        </div>
                        <div class="data__tender data-row__item" v-show="showTender">
                            <p class="tender__amount__text" v-html="tenderText"></p>
                        </div>
                    </div>

                    <div class="flex justify-between w-full mt-4 items-center">
                        <div class="data__info-date" v-if="infoDate">
                            <p class="info-date__text">{{ infoDate }}</p>
                        </div>
                        <div class="data__download">
                            <a class="inline-block rounded-2xl border-1 border-black text-xs px-3 py-1 whitespace-nowrap hover:bg-black hover:text-white download-data__link" :href="exportUrl">{{ i18n.downloadData }}</a>
                        </div>
                    </div>
                </div>

            </div><!-- .data__container -->

            <!-- Countries list -->
            <ul class="list list--country" v-show="panel === 'list'">
                <li class="country" v-for="country in countries" :key="country.id">
                    <h4 class="country__title" @click="onCountryTitleClick(country)">
                        {{ countryLabel(country) }}
                    </h4>
                    <ul class="list list--city">
                        <li class="city__title"
                            v-for="city in country.cities"
                            :key="city.slug"
                            @click="onCityTitleClick(city, country)">
                            {{ cityLabel(city) }}
                        </li>
                    </ul>
                </li>
            </ul>

        </div><!-- .side-content -->

    </div><!-- .section--home -->
</div>
</template>
